import React, { Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Loader from '@athena/spark/Loader';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'src/components/provider/AppStateProvider';
import { useAppointmentState } from 'src/components/provider/AppointmentProvider';
import SessionProvider, { useSessionState } from 'src/components/provider/SessionProvider';
import WelcomePage from 'src/components/page/WelcomePage';
import InvalidAppointmentPage from 'src/components/page/InvalidAppointmentPage';
import { RoutePaths } from './routeMap';

const ServiceChime = React.lazy(
  () =>
    import(
      /* webpackChunkName: "service-chime" */
      /* webpackPrefetch: true */
      './ServiceChime'
    )
);

const ServiceZoom = React.lazy(
  () =>
    import(
      /* webpackChunkName: "service-zoom" */
      /* webpackPrefetch: true */
      './ServiceZoom'
    )
);

const CallSummaryPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "post-welcome" */
      /* webpackPrefetch: true */
      '../components/page/CallSummaryPage'
    )
);

export const useGoToWelcomePage = () => {
  const navigate = useNavigate();
  const goToWelcomePage = () => {
    const sessionJoinToken = sessionStorage.getItem('joinToken');
    navigate(RoutePaths.Welcome.replace(':token', sessionJoinToken as string), { replace: true });
  };
  return goToWelcomePage;
};

const DeeplinkRoute = () => {
  const navigate = useNavigate();
  const { setAppState } = useAppState();
  const { appointmentId, invalidAppt, validateDeeplinkCode, setJoinToken } = useAppointmentState();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchDeeplinkApptData = async () => {
      const deeplinkResponse = await validateDeeplinkCode();
      if (deeplinkResponse) {
        const { appointmentToken, displayName } = deeplinkResponse;
        sessionStorage.setItem('joinToken', appointmentToken);
        setAppState({ attendeeName: displayName, arrivedViaDeeplink: true });
        setJoinToken(appointmentToken);
      } else {
        // this is kept as low as possible so that setAppState isn't called twice in the happy path causing unnecessary/expensive re-renders
        setAppState({ arrivedViaDeeplink: true });
        navigate(RoutePaths.InvalidAppointment);
      }
    };
    fetchDeeplinkApptData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // will run after a the appointment api call returns a valid appointId or reports an invalid appointment
  useEffect(() => {
    if (appointmentId) {
      const nextRoute = process.env.SKIP_DEVICE_CHECKS === 'True' ? RoutePaths.WaitingRoom : RoutePaths.ReadinessCheck;
      navigate(nextRoute, { replace: true });
    } else if (invalidAppt) {
      navigate(RoutePaths.InvalidAppointment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, invalidAppt]);

  return <Loader loading text={t('LOADING_APPOINTMENT_2')} />;
};

const WithRedirect: React.FC<WithChildren> = ({ children }) => {
  const { attendeeName } = useAppState();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const goToWelcomePage = useGoToWelcomePage();
  const sessionJoinToken = sessionStorage.getItem('joinToken');

  useEffect(() => {
    // the route was accessed via page refresh or direct input into url bar
    if (!attendeeName) {
      // the presence of a join token in local storage indicates the user initially started on the welcome page or from a deeplink
      if (sessionJoinToken) {
        goToWelcomePage();
      } else {
        navigate(RoutePaths.InvalidAppointment);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  if (attendeeName) {
    return <>{children}</>;
  } else {
    return null;
  }
};

export const InCallRouteService = () => {
  const { t } = useTranslation();
  const { jwt, thVendor } = useSessionState();

  const LoadingUI = () => <Loader loading={true} text={t('LOADING_APPOINTMENT_3')} />;
  if (jwt) {
    const Service = thVendor === 'zoom' ? ServiceZoom : ServiceChime;
    return (
      <Suspense fallback={<LoadingUI />}>
        <Service />
      </Suspense>
    );
  } else {
    return <LoadingUI />;
  }
};

const InCallRoute = () => {
  return (
    <WithRedirect>
      <SessionProvider>
        <InCallRouteService />
      </SessionProvider>
    </WithRedirect>
  );
};

export const TelehealthRoutes = () => {
  const location = useLocation();
  const { setAppState } = useAppState();
  const { invalidAppt } = useAppointmentState();
  const { t } = useTranslation();

  useEffect(() => {
    switch (location.pathname) {
      case RoutePaths.Welcome:
        setAppState({ showHeader: true, showFooter: true, documentTitle: t(`TITLE_VISIT_SIGN_IN`) });
        break;
      case RoutePaths.MeetingSummary: {
        setAppState({ showHeader: true, showFooter: true, documentTitle: t(`TITLE_VISIT_ENDED`) });
        break;
      }
      case RoutePaths.InvalidAppointment: {
        setAppState({ documentTitle: t(`TITLE_INVALID_VISIT_LINK`) });
        break;
      }
    }
  }, [location, setAppState, t]);

  return (
    <Routes>
      <Route path={RoutePaths.Deeplink} element={<DeeplinkRoute />} />
      <Route
        path={RoutePaths.Welcome}
        element={invalidAppt ? <Navigate to={RoutePaths.InvalidAppointment} /> : <WelcomePage />}
      />
      <Route path={RoutePaths.InvalidAppointment} element={<InvalidAppointmentPage />} />
      <Route path="/in-call/*" element={<InCallRoute />} />
      <Route
        path={RoutePaths.MeetingSummary}
        element={
          <WithRedirect>
            <Suspense fallback={<div></div>}>
              <CallSummaryPage />
            </Suspense>
          </WithRedirect>
        }
      />
      <Route path="*" element={<Navigate to={RoutePaths.InvalidAppointment} />} />
    </Routes>
  );
};
